'use client';

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import { track } from '@/lib/telemetry';
import { useSWRConfig } from 'swr';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { ScrollRail } from '@/components/ui/scroll-rail';
import { CheckIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import type { CategoryOverviewRow } from '@/types';
import { ApiError, categoryBudgetApi } from '@/lib/api';
import { CategoryIcon } from '@/components/CategoryIcon';
import { CategoryRingTile } from '@/components/categories/CategoryRingTile';
import { CategoryActionsMenu } from '@/components/orcamento/CategoryActionsMenu';
import { NewCategorySheet, type EditableCategory } from '@/components/orcamento/NewCategorySheet';
import { InlineLimitEditor } from '@/components/categories/InlineLimitEditor';
import { UNCATEGORIZED_CATEGORY_ID, applyLimitToOverview, monthlyLimitFromScaled } from '@/components/orcamento/helpers';
import { useRuleRefs } from '@/components/orcamento/useRuleRefs';
import { useCategoryOverview } from '@/hooks/queries/useCategoryOverview';
import { revalidateCustomCategories, revalidateTransactionCaches, useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { CategoryDetailContent, type CategoryDetailView } from '../category-detail/CategoryDetailDrawer';
import {
  type CategoryIndex,
  type CategoryMeta,
  categoryChipStyleFromMeta,
  getCategoryMeta,
} from './categoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn, normalizeForSearch } from '@/lib/utils';
import type { ScreenTransaction } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: readonly string[];
  onApply: (value: string[]) => void;
  /** Live preview of how many rows will show given the *pending* selection. */
  previewCount: (pending: readonly string[]) => number;
  /** Map from category id → share-% within the current visible category scope (0..100). */
  sharesByCategoryId: ReadonlyMap<string, number>;
  /** Map from category id → total absolute amount (positive number) within the current scope. */
  amountsByCategoryId: ReadonlyMap<string, number>;
  /** Stub list of last-used category ids; real signal lands in M5. */
  recentCategoryIds: readonly string[];
  /**
   * Categories with at least one transaction in the current visible scope.
   * Items outside this set are hidden — except those that are currently
   * selected (so the user can always deselect them).
   */
  availableCategoryIds: ReadonlySet<string>;
  /** Server-derived metadata (label, icon, color) per category id. */
  categoryIndex: CategoryIndex;
  // --- Detail (breakdown) view ---
  /** Period being viewed; forwarded to the in-place breakdown view. */
  dateRange: { from: Date; to: Date };
  /** Apply a single parent-category filter (from the breakdown's CTA). */
  onApplyParentCategoryFilter: (categoryId: string) => void;
  /** Apply an exact child-category filter (from the breakdown's CTA). */
  onApplyChildCategoryFilter: (categoryId: string) => void;
  /** Open a transaction's detail (full tx — see CategoryDetailContent's handoff contract). */
  onOpenTransactionDetails?: (tx: ScreenTransaction) => void;
}

function pluralize(n: number): string {
  if (n === 0) return 'Nenhum lançamento';
  if (n === 1) return 'Mostrar 1 lançamento';
  return `Mostrar ${n} lançamentos`;
}

export function CategoryFilterDrawer({
  open,
  onOpenChange,
  value,
  onApply,
  previewCount,
  sharesByCategoryId,
  amountsByCategoryId,
  recentCategoryIds,
  availableCategoryIds,
  categoryIndex,
  dateRange,
  onApplyParentCategoryFilter,
  onApplyChildCategoryFilter,
  onOpenTransactionDetails,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const [pending, setPending] = useState<Set<string>>(() => new Set(value));
  const [query, setQuery] = useState('');
  // When set, the drawer shows the in-place breakdown for this category.
  const [detailCategoryId, setDetailCategoryId] = useState<string | null>(null);
  // Which hub view the in-place detail opens on ("Ver lançamentos" → the list,
  // "Regras" → the rules view).
  const [detailView, setDetailView] = useState<CategoryDetailView>('transactions');
  // Kebab actions (parity with the Categorias page rows).
  const [editingLimitRef, setEditingLimitRef] = useState<string | null>(null);
  const [savingLimit, setSavingLimit] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EditableCategory | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);

  // Pending selections follow the applied value (re-synced if it changes while open).
  useEffect(() => {
    if (open) setPending(new Set(value));
  }, [open, value]);

  // Navigation state resets ONLY on (re)open — never on a mid-open `value` identity
  // change (any host re-render can produce one). Resetting here would yank the user
  // out of the in-place category detail, e.g. while the stacked transaction detail
  // drawer is open on top of it.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDetailCategoryId(null);
      setDetailView('transactions');
      setEditingLimitRef(null);
    }
  }, [open]);

  // --- Kebab parity with the Categorias page: same CategoryActionsMenu controls. ---
  const authedFetch = useAuthedFetcher();
  const { mutate: mutateGlobal } = useSWRConfig();
  const ruleRefs = useRuleRefs();
  // Overview rows carry the kebab metadata (source, limit, archived, média 3m).
  // Fetched only while the drawer is open, keyed by the visible period.
  const { data: overview, mutate: mutateOverview } = useCategoryOverview(
    open ? { from: dateRange.from, to: dateRange.to } : null,
  );
  const overviewByRef = useMemo(
    () => new Map((overview?.rows ?? []).map((r) => [r.categoryRef, r])),
    [overview],
  );
  const resolveOverviewRow = (meta: CategoryMeta): CategoryOverviewRow => {
    const ref = meta.id === 'uncategorized' ? UNCATEGORIZED_CATEGORY_ID : meta.id;
    // Fallback while the overview loads (or for ids it doesn't know): enough for the
    // menu — pluggy source hides edit/archive, no limit, not archived.
    return (
      overviewByRef.get(ref) ?? {
        categoryRef: ref,
        source: 'pluggy',
        name: meta.label,
        icon: meta.iconName,
        color: null,
        kind: 'expense',
        spent: 0,
        average3mo: 0,
        limit: null,
        burnPercent: null,
        status: 'no_limit',
        txCount: 0,
        needsReviewCount: 0,
        isArchived: false,
      }
    );
  };

  const showError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.isLimitExceeded()) return; // global upgrade dialog already shown
    notifyError('category_filter', err, fallback);
  };

  const commitLimit = async (categoryRef: string, parsed: number | null) => {
    setSavingLimit(true);
    try {
      if (overview) {
        await mutateOverview(
          async (current) => {
            await authedFetch((token) =>
              categoryBudgetApi.upsertLimit(token, categoryRef, { limitAmount: parsed }),
            );
            return applyLimitToOverview(current ?? overview, categoryRef, parsed);
          },
          {
            optimisticData: (current) => applyLimitToOverview(current ?? overview, categoryRef, parsed),
            rollbackOnError: true,
            populateCache: true,
            // Background refetch corrects summary/burn from the server.
            revalidate: true,
          },
        );
      } else {
        // The editor is reachable from the kebab before the overview resolves — the
        // fallback row has nothing to patch, so save plainly and let SWR refetch.
        await authedFetch((token) =>
          categoryBudgetApi.upsertLimit(token, categoryRef, { limitAmount: parsed }),
        );
        void mutateOverview();
      }
      track('action.budget.set_limit', { surface: 'category_filter', cleared: parsed == null });
      toast.success(parsed == null ? 'Limite removido.' : 'Limite salvo.');
      setEditingLimitRef(null);
    } catch (err) {
      showError(err, 'Não foi possível salvar o limite.');
    } finally {
      setSavingLimit(false);
    }
  };

  const handleUpdateCategory = async (
    id: string,
    data: { name: string; icon: string | null; color: string | null },
  ) => {
    setSavingCategory(true);
    try {
      await authedFetch((token) => categoryBudgetApi.updateCategory(token, id, data));
      track('action.category.update', { surface: 'category_filter' });
      toast.success('Categoria atualizada.');
      setEditingCategory(null);
      void mutateOverview();
      // The new name/color shows in this drawer and in the list chips via the meta caches.
      revalidateTransactionCaches(mutateGlobal);
      // CategoryPickerDrawer reads customs from ['custom-categories'], not from overview
      // rows — without this it can reopen showing the stale (pre-rename) name.
      revalidateCustomCategories(mutateGlobal);
    } catch (err) {
      showError(err, 'Não foi possível atualizar a categoria.');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleToggleArchived = async (row: CategoryOverviewRow) => {
    try {
      // PUT replaces name, icon and color, so the row goes back as it is.
      await authedFetch((token) =>
        categoryBudgetApi.updateCategory(token, row.categoryRef, {
          name: row.name, icon: row.icon, color: row.color, archived: !row.isArchived,
        }),
      );
      track(row.isArchived ? 'action.category.unarchive' : 'action.category.archive', {
        surface: 'category_filter',
      });
      toast.success(row.isArchived
        ? `“${row.name}” desarquivada.`
        : `“${row.name}” arquivada — o histórico fica preservado.`);
      void mutateOverview();
      revalidateTransactionCaches(mutateGlobal);
      revalidateCustomCategories(mutateGlobal);
    } catch (err) {
      showError(err, 'Não foi possível arquivar a categoria.');
    }
  };

  // Categories visible in the picker: those present in the current scope,
  // plus any currently-selected ids (so users can always deselect them).
  const visibleCategoryIds = useMemo(() => {
    const out = new Set<string>(availableCategoryIds);
    for (const v of value) out.add(v);
    return out;
  }, [availableCategoryIds, value]);

  // Flat list, sorted by share desc, falling back to label.
  const sortedCategories = useMemo<CategoryMeta[]>(() => {
    const items: CategoryMeta[] = [];
    for (const id of visibleCategoryIds) {
      items.push(getCategoryMeta(categoryIndex, id));
    }
    items.sort((a, b) => {
      const sa = sharesByCategoryId.get(a.id) ?? 0;
      const sb = sharesByCategoryId.get(b.id) ?? 0;
      if (sb !== sa) return sb - sa;
      return a.label.localeCompare(b.label, 'pt-BR');
    });
    return items;
  }, [visibleCategoryIds, categoryIndex, sharesByCategoryId]);

  const recentChips = useMemo<CategoryMeta[]>(
    () =>
      recentCategoryIds
        .filter((id) => visibleCategoryIds.has(id))
        .map((id) => getCategoryMeta(categoryIndex, id))
        .filter((m) => m.id !== 'uncategorized'),
    [recentCategoryIds, visibleCategoryIds, categoryIndex],
  );

  const toggle = (id: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const liveCount = previewCount(Array.from(pending));

  const filteredCategories = useMemo(() => {
    if (query.length === 0) return sortedCategories;
    const needle = normalizeForSearch(query);
    return sortedCategories.filter((c) =>
      normalizeForSearch(c.label).includes(needle),
    );
  }, [sortedCategories, query]);

  const showingDetail = detailCategoryId !== null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        data-tokens="v2"
        className="flex size-full min-h-0 flex-col overflow-hidden bg-surface px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
      >
        {showingDetail ? (
          <CategoryDetailContent
            active={open}
            categoryId={detailCategoryId}
            initialView={detailView}
            categoryIndex={categoryIndex}
            dateRange={dateRange}
            categoryAmount={detailCategoryId ? amountsByCategoryId.get(detailCategoryId) : undefined}
            categorySharePct={detailCategoryId ? sharesByCategoryId.get(detailCategoryId) : undefined}
            onApplyParentCategoryFilter={onApplyParentCategoryFilter}
            onApplyChildCategoryFilter={onApplyChildCategoryFilter}
            onOpenTransactionDetails={onOpenTransactionDetails}
            onEditCategory={(row) => setEditingCategory({
              id: row.categoryRef,
              name: row.name,
              icon: row.icon,
              color: row.color,
            })}
            onBack={() => setDetailCategoryId(null)}
          />
        ) : (
          <>
            <DrawerHeader className="flex flex-row items-center justify-between px-4 py-3">
              <DrawerTitle className="text-lg font-semibold text-text-primary">Categorias</DrawerTitle>
              <DrawerDescription className="sr-only">
                Selecione as categorias usadas para filtrar os lançamentos ou toque em uma para ver os detalhes.
              </DrawerDescription>
              <button
                type="button"
                className="text-sm text-text-secondary hover:text-text-primary"
                onClick={() => setPending(new Set())}
              >
                Limpar
              </button>
            </DrawerHeader>

            <div className="flex items-center gap-2 border-t border-border px-3 py-2">
              <div className="flex flex-1 items-center gap-2 rounded-md bg-surface-sunken px-3">
                <MagnifyingGlassIcon className="size-4 text-text-tertiary" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar categoria"
                  className="h-9 w-full bg-transparent text-base outline-none placeholder:text-text-tertiary"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {query.length === 0 && recentChips.length > 0 && (
                <div className="px-3 pt-2 pb-1">
                  <p className="px-1 pb-2 text-2xs font-bold tracking-wider text-text-tertiary uppercase">
                    Recentes
                  </p>
                  <ScrollRail railClassName="gap-2 pb-1">
                    {recentChips.map((c) => {
                      const style = categoryChipStyleFromMeta(c);
                      const selected = pending.has(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggle(c.id)}
                          className={cn(
                            'flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                            selected ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-surface text-text-primary',
                          )}
                        >
                          <span
                            className="flex size-5 items-center justify-center rounded-md"
                            style={{ backgroundColor: style.backgroundColor }}
                          >
                            <CategoryIcon name={c.iconName} className="size-3" style={{ color: style.color }} />
                          </span>
                          {c.label}
                        </button>
                      );
                    })}
                  </ScrollRail>
                </div>
              )}

              <ul className="divide-y divide-divider" role="list">
                {filteredCategories.length === 0 ? (
                  <li className="px-4 py-10 text-center text-sm text-text-tertiary">
                    Nenhuma categoria encontrada
                  </li>
                ) : (
                  filteredCategories.map((c) => {
                    const style = categoryChipStyleFromMeta(c);
                    const selected = pending.has(c.id);
                    const share = sharesByCategoryId.get(c.id) ?? 0;
                    const amount = amountsByCategoryId.get(c.id) ?? 0;
                    const hasAmount = amount > 0;
                    const hasShare = sharesByCategoryId.has(c.id);
                    const pctText = share >= 0.1 ? `${share.toFixed(share < 10 ? 1 : 0)}%` : '<0,1%';
                    const overviewRow = resolveOverviewRow(c);

                    // Inline limit editor (kebab → Definir/Alterar limite) — its own
                    // layout so the input doesn't nest inside the select button.
                    if (editingLimitRef === overviewRow.categoryRef) {
                      return (
                        <li key={c.id} className="flex items-center gap-3 py-3 pr-2 pl-4">
                          <CategoryRingTile
                            icon={c.iconName}
                            visual={style}
                            percent={hasShare ? share : null}
                            ringColor={style.accent}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-text-primary">
                              {c.label}
                            </span>
                            <span className="mt-1.5 flex items-center gap-1.5">
                              <InlineLimitEditor
                                size="sm"
                                // Same prefill as the Categorias page: an empty limit seeds from the
                                // 3-month average. The editor edits MONTHLY values, but a multi-month
                                // period shows range-scaled limits — convert the seed back.
                                initialValue={
                                  monthlyLimitFromScaled(overviewRow.limit, overview?.budgetMonths ?? 1)
                                    ?? (overviewRow.average3mo > 0 ? overviewRow.average3mo : null)
                                }
                                saving={savingLimit}
                                onCommit={(value) => void commitLimit(overviewRow.categoryRef, value)}
                                onCancel={() => setEditingLimitRef(null)}
                              />
                            </span>
                            <span className="mt-1 block text-2xs text-text-tertiary">
                              Limite mensal da categoria
                              {overviewRow.limit != null ? ' · 0 ou vazio remove o limite' : ''}
                            </span>
                          </span>
                        </li>
                      );
                    }

                    return (
                      <li
                        key={c.id}
                        className={cn(
                          'flex items-center gap-1 pr-2',
                          selected ? 'bg-primary/5' : 'hover:bg-surface-sunken',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => toggle(c.id)}
                          aria-pressed={selected}
                          aria-label={`${selected ? 'Remover' : 'Selecionar'} ${c.label}`}
                          className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left"
                        >
                          <span
                            className={cn(
                              'flex size-5 shrink-0 items-center justify-center rounded-md border',
                              selected
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border-strong bg-surface',
                            )}
                            aria-hidden
                          >
                            {selected && <CheckIcon className="size-3.5" weight="bold" />}
                          </span>
                          {/* Unified row tile (shared with the Categorias page): the ring
                              carries the category's share of the visible total. */}
                          <CategoryRingTile
                            icon={c.iconName}
                            visual={style}
                            percent={hasShare ? share : null}
                            ringColor={style.accent}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-text-primary">
                              {c.label}
                            </span>
                            {(hasAmount || hasShare || overviewRow.limit != null) && (
                              <span className="mt-0.5 block text-xs text-text-tertiary">
                                {hasAmount && (
                                  <span className="font-mono font-medium text-text-secondary tabular-nums">
                                    {formatCurrency(amount)}
                                  </span>
                                )}
                                {hasAmount && hasShare && <span className="px-1">·</span>}
                                {hasShare && <span className="font-mono tabular-nums">{pctText}</span>}
                                {overviewRow.limit != null && (
                                  <>
                                    {(hasAmount || hasShare) && <span className="px-1">·</span>}
                                    <span className="font-mono tabular-nums">
                                      limite {formatCurrency(overviewRow.limit)}
                                    </span>
                                  </>
                                )}
                              </span>
                            )}
                          </span>
                        </button>
                        {/* Same kebab as the Categorias page rows; "Ver lançamentos"
                            and "Regras" open the in-place hub on the matching view. */}
                        <CategoryActionsMenu
                          row={overviewRow}
                          hasRules={ruleRefs.has(c.id)}
                          canEditLimit={overviewRow.kind === 'expense' && !overviewRow.isArchived}
                          onEdit={(row) => setEditingCategory({
                            id: row.categoryRef,
                            name: row.name,
                            icon: row.icon,
                            color: row.color,
                          })}
                          onEditLimit={() => setEditingLimitRef(overviewRow.categoryRef)}
                          onViewTransactions={() => {
                            setDetailView('transactions');
                            setDetailCategoryId(c.id);
                          }}
                          onViewRules={() => {
                            setDetailView('rules');
                            setDetailCategoryId(c.id);
                          }}
                          onToggleArchived={handleToggleArchived}
                        />
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div className="border-t border-border px-4 pt-3">
              <button
                type="button"
                onClick={() => {
                  onApply(Array.from(pending));
                  onOpenChange(false);
                }}
                className="h-12 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                disabled={liveCount === 0}
              >
                {pluralize(liveCount)}
              </button>
            </div>
          </>
        )}

        {/* Kebab-hosted surfaces (portalled overlays): the same edit sheet and rules
            drawer the Categorias page uses. This host is edit-only — create is not
            reachable from the kebab, so onCreate is a no-op. */}
        <NewCategorySheet
          open={editingCategory != null}
          onOpenChange={(o) => {
            if (!o) setEditingCategory(null);
          }}
          saving={savingCategory}
          onCreate={async () => {}}
          editing={editingCategory}
          onUpdate={handleUpdateCategory}
        />
      </DrawerContent>
    </Drawer>
  );
}
